import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import {
  TrophyIcon,
  RocketLaunchIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

import { useUser } from './userContext';

function Sidebar({
  sidebarOpen,
  setSidebarOpen,
  variant = 'default',
}) {
  const location = useLocation();
  const { pathname } = location;
  const { user, userProfile, businessProfile, campaigns } = useUser();
  const userName = userProfile?.firstName;
  const isImpersonating = businessProfile?.id && userProfile?.companyId && businessProfile.id !== userProfile.companyId;
  
  // Calculate game count for conditional navigation display
  const totalGamesCount = campaigns?.length || 0;

  // Admin permission logic (same as AdminRoute.jsx)
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const isDevSite = window.location.hostname === 'smilesflowdev-1514f.web.app';
  const isProduction = window.location.hostname === 'www.smilesflow.com' || window.location.hostname === 'smilesflow.com';
  
  // Check for admin role in userProfile, with fallback to email for localhost/dev
  const hasAdminRole = userProfile?.role === 'admin';
  const isAdminEmail = userProfile?.email === 'support@smilesflow.com' || user?.email === 'support@smilesflow.com';
  
  // Allow admin role on all environments, or admin email on localhost/dev as fallback
  const isAdmin = hasAdminRole || ((isLocalhost || isDevSite) && isAdminEmail);

  const trigger = useRef(null);
  const sidebar = useRef(null);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(storedSidebarExpanded === null ? false : storedSidebarExpanded === "true");

  // Suppress CSS transitions on initial mount so sidebar doesn't flash/animate in
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // Set body class synchronously on first paint, then enable transitions
    if (sidebarExpanded) {
      document.body.classList.add("sidebar-expanded");
    } else {
      document.body.classList.remove("sidebar-expanded");
    }
    requestAnimationFrame(() => setMounted(true));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Portal-based tooltip for collapsed sidebar (escapes overflow-y-auto)
  const [tooltip, setTooltip] = useState(null);
  const showTooltip = useCallback((e, label) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({ label, top: rect.top + rect.height / 2, left: rect.right + 8 });
  }, []);
  const hideTooltip = useCallback(() => setTooltip(null), []);

  // close on click outside
  useEffect(() => {
    const clickHandler = ({ target }) => {
      if (!sidebar.current || !trigger.current) return;
      if (!sidebarOpen || sidebar.current.contains(target) || trigger.current.contains(target)) return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }) => {
      if (!sidebarOpen || keyCode !== 27) return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  // close mobile sidebar on route change
  useEffect(() => {
    if (sidebarOpen) setSidebarOpen(false);
  }, [pathname]);

  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded);
    if (sidebarExpanded) {
      document.querySelector("body").classList.add("sidebar-expanded");
    } else {
      document.querySelector("body").classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  return (
    <div className="min-w-fit">
      {/* Sidebar backdrop (mobile only) */}
      <div
        className={`fixed inset-0 bg-black/40 z-40 lg:hidden lg:z-auto transition-opacity duration-200 ${
          sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden="true"
      ></div>

      {/* Sidebar – Group Chat Heroes (see docs/DESIGN_SYSTEM.md) */}
      <div
        id="sidebar"
        ref={sidebar}
        className={`gch-sidebar-header flex lg:!flex flex-col absolute z-40 left-0 top-0 lg:static lg:left-auto lg:top-auto lg:translate-x-0 h-[100dvh] overflow-y-auto w-64 lg:w-20 lg:sidebar-expanded:!w-64 2xl:!w-64 shrink-0 p-4 ${mounted ? 'transition-all duration-200 ease-in-out' : ''} studio-scrollbar ${sidebarOpen ? "translate-x-0" : "-translate-x-64"}`}
      >
        {/* Sidebar header */}
        <div className="flex justify-between items-center mb-4 pr-1 sm:px-1">
          <button
            ref={trigger}
            className="lg:hidden gch-text-secondary hover:text-white transition-colors"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-controls="sidebar"
            aria-expanded={sidebarOpen}
          >
            <span className="sr-only">Close sidebar</span>
            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.7 18.7l1.4-1.4L7.8 13H20v-2H7.8l4.3-4.3-1.4-1.4L4 12z" />
            </svg>
          </button>
          
          <NavLink end to="/studio/dashboard" className="block w-full">
            <div className="flex justify-center -mt-1 mb-4">
              <img src="/mutedLogoFavicon.png" alt="SmilesFlow" className="h-10 w-auto object-contain mx-auto" />
            </div>
          </NavLink>
        </div>


        {/* Navigation Links */}
        <div className="space-y-2 flex-1">
          {/* Dashboard */}
          <NavLink
            end
            to="/studio/dashboard"
            className={({ isActive }) =>
              `flex items-center px-3 py-3 rounded-xl text-[13px] font-semibold transition-all duration-200 ${
                isActive
                  ? isImpersonating
                    ? 'text-white bg-white/[0.08] ring-1 ring-inset ring-white/15'
                    : 'text-white bg-[#F4B942]/15'
                  : 'gch-text-secondary hover:text-white hover:bg-white/5'
              }`
            }
            onMouseEnter={(e) => !sidebarExpanded && showTooltip(e, 'Dashboard')}
            onMouseLeave={hideTooltip}
          >
            <RocketLaunchIcon className="h-5 w-5 shrink-0" />
            <span className="ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
              Dashboard
            </span>
          </NavLink>

          {/* Campaigns */}
          <NavLink
            end
            to="/studio/campaigns"
            className={({ isActive }) =>
              `flex items-center px-3 py-3 rounded-xl text-[13px] font-semibold transition-all duration-200 ${
                isActive
                  ? isImpersonating
                    ? 'text-white bg-white/[0.08] ring-1 ring-inset ring-white/15'
                    : 'text-white bg-[#F4B942]/15'
                  : 'gch-text-secondary hover:text-white hover:bg-white/5'
              }`
            }
            onMouseEnter={(e) => !sidebarExpanded && showTooltip(e, 'Campaigns')}
            onMouseLeave={hideTooltip}
          >
            <TrophyIcon className="h-5 w-5 shrink-0" />
            <span className="ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
              Campaigns
            </span>
          </NavLink>

{/* Game Templates */}
          <NavLink
            end
            to="/studio/games"
            className={({ isActive }) =>
              `flex items-center px-3 py-3 rounded-xl text-[13px] font-semibold transition-all duration-200 ${
                isActive
                  ? isImpersonating
                    ? 'text-white bg-white/[0.08] ring-1 ring-inset ring-white/15'
                    : 'text-white bg-[#F4B942]/15'
                  : 'gch-text-secondary hover:text-white hover:bg-white/5'
              }`
            }
            onMouseEnter={(e) => !sidebarExpanded && showTooltip(e, 'Game Templates')}
            onMouseLeave={hideTooltip}
          >
            <Squares2X2Icon className="h-5 w-5 shrink-0" />
            <span className="ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
              Game Templates
            </span>
          </NavLink>

          {/* Credits moved to the top header pill (Header.jsx) so it's
              visible everywhere including mobile, where the sidebar is
              hidden behind a hamburger. */}

          {/* Admin - only visible to admin users */}
          {isAdmin && (
            <NavLink
              end
              to="/studio/admin"
              className={({ isActive }) =>
                `flex items-center px-3 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? isImpersonating
                      ? 'text-white bg-white/[0.08] ring-1 ring-inset ring-white/15'
                      : 'text-white bg-[#F4B942]/15'
                    : 'gch-text-secondary hover:text-white hover:bg-white/5'
                }`
              }
              onMouseEnter={(e) => !sidebarExpanded && showTooltip(e, 'Admin')}
              onMouseLeave={hideTooltip}
            >
              <ShieldCheckIcon className="h-5 w-5 shrink-0" />
              <span className="ml-3 lg:opacity-0 lg:sidebar-expanded:opacity-100 2xl:opacity-100 duration-200">
                Admin
              </span>
            </NavLink>
          )}
        </div>

        {/* Bottom bar: Settings + Expand/collapse */}
        <div className="mt-auto pt-4 mx-1 border-t border-white/[0.06]">
          <div className="flex lg:flex-col lg:items-center lg:sidebar-expanded:flex-row lg:sidebar-expanded:items-center lg:sidebar-expanded:justify-between 2xl:flex-row 2xl:items-center 2xl:justify-between items-center justify-between px-1 pb-1 gap-1">
            <NavLink
              to="/studio/settings/account"
              className={() =>
                `w-9 h-9 flex items-center justify-center rounded-lg transition-all duration-200 ${
                  pathname.includes('settings')
                    ? 'bg-white/10 text-white'
                    : 'gch-text-muted hover:text-white hover:bg-white/[0.06]'
                }`
              }
              onMouseEnter={(e) => !sidebarExpanded && showTooltip(e, 'Settings')}
              onMouseLeave={hideTooltip}
            >
              <span className="sr-only">Settings</span>
              <Cog6ToothIcon className="h-[18px] w-[18px] shrink-0" />
            </NavLink>
            <button
              type="button"
              className="w-9 h-9 hidden lg:flex 2xl:hidden items-center justify-center rounded-lg gch-text-muted hover:text-white hover:bg-white/[0.06] transition-all duration-200 cursor-pointer"
              onClick={() => setSidebarExpanded(!sidebarExpanded)}
            >
              <span className="sr-only">Expand / collapse sidebar</span>
              <svg
                className={`shrink-0 w-[14px] h-[14px] fill-current transition-transform duration-200 ${sidebarExpanded ? 'rotate-180' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
              >
                <path d="M15 16a1 1 0 0 1-1-1V1a1 1 0 1 1 2 0v14a1 1 0 0 1-1 1ZM8.586 7H1a1 1 0 1 0 0 2h7.586l-2.793 2.793a1 1 0 1 0 1.414 1.414l4.5-4.5A.997.997 0 0 0 12 8.01M11.924 7.617a.997.997 0 0 0-.217-.324l-4.5-4.5a1 1 0 0 0-1.414 1.414L8.586 7M12 7.99a.996.996 0 0 0-.076-.373Z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Portal tooltip  - renders outside sidebar overflow container */}
      {tooltip && !sidebarExpanded && createPortal(
        <div
          className="fixed z-[9999] px-2.5 py-1.5 text-xs font-medium rounded-lg gch-bg-card gch-text-primary border border-[var(--admin-border)] shadow-lg whitespace-nowrap pointer-events-none"
          style={{ top: tooltip.top, left: tooltip.left, transform: 'translateY(-50%)', fontFamily: '"Inter", sans-serif' }}
        >
          {tooltip.label}
        </div>,
        document.body
      )}
    </div>
  );
}

export default Sidebar;
